import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, del } from "../../lib/request"

function normalizeMessageIds(messageIds) {
  if (messageIds == null) return []
  const raw = Array.isArray(messageIds) ? messageIds : [messageIds]
  const seen = new Set()
  const out = []
  for (const id of raw) {
    if (id == null || String(id).trim() === "") continue
    const key = String(id)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

/**
 * GET TAGS — accepts an array of messageIds, returns tags for all
 * /api/v1/message-tag?messageIds=1029,1031,...
 */
export const getMessageTags = createAsyncThunk(
    "messageTags/getMessageTags",
    async (messageIds, { rejectWithValue, getState }) => {
        try {
            const allIds = normalizeMessageIds(messageIds)
            const { fetchedMessageIds } = getState().messageTags
            const missing = allIds.filter((id) => !fetchedMessageIds[id])
            if (missing.length === 0) return { tags: [], fetchedIds: [] }
            const ids = missing.join(',')
            const response = await get(`/message-tags?messageIds=${ids}`)
            return {
              tags: response.data || response || [],
              fetchedIds: missing,
            }
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch message tags")
        }
    },
    {
      condition: (messageIds, { getState }) => {
        const allIds = normalizeMessageIds(messageIds)
        if (allIds.length === 0) return false
        const { fetchedMessageIds } = getState().messageTags
        return allIds.some((id) => !fetchedMessageIds[id])
      },
    },
)

/**
 * ADD TAG
 * POST /api/v1/message-tag
 */
export const addMessageTag = createAsyncThunk(
  "messageTags/addMessageTag",
  async ({ messageId, name }, { rejectWithValue }) => {
    try {
      const response = await post("/message-tags", { messageId, name })
      return response.data || response
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add tag")
    }
  }
)

/**
 * DELETE TAG
 * DELETE /api/v1/message-tag/{id}
 */
export const deleteMessageTag = createAsyncThunk(
  "messageTags/deleteMessageTag",
  async ({ tagId }, { rejectWithValue }) => {
    try {
      await del(`/message-tags/${tagId}`)
      return { tagId }
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete tag")
    }
  }
)

const messageTagSlice = createSlice({
    name: "messageTags",
    initialState: {
        tagsByMessageId: {},
        fetchedMessageIds: {},
        status: "idle",
        error: null,
    },
    reducers: {
        clearAllMessageTags: () => ({
            tagsByMessageId: {},
            fetchedMessageIds: {},
            status: "idle",
            error: null,
        }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMessageTags.pending, (state) => {
                state.status = "loading"
            })
            .addCase(getMessageTags.fulfilled, (state, action) => {
                state.status = "succeeded"
                const { tags, fetchedIds } = action.payload
                for (const id of fetchedIds || []) {
                  state.fetchedMessageIds[String(id)] = true
                }
                if (!Array.isArray(tags)) return
                const grouped = {}
                for (const t of tags) {
                    const mid = t.messageId
                    if (mid == null) continue
                    if (!grouped[mid]) grouped[mid] = []
                    grouped[mid].push(t)
                }
                Object.assign(state.tagsByMessageId, grouped)
            })
            .addCase(getMessageTags.rejected, (state, action) => {
                state.status = "failed"
                state.error = action.payload
            })
            .addCase(addMessageTag.fulfilled, (state, action) => {
                const tag = action.payload
                if (tag?.messageId != null) {
                    const mid = String(tag.messageId)
                    state.fetchedMessageIds[mid] = true
                    if (!state.tagsByMessageId[mid]) {
                        state.tagsByMessageId[mid] = []
                    }
                    state.tagsByMessageId[mid].push(tag)
                }
            })
            .addCase(deleteMessageTag.fulfilled, (state, action) => {
                const { tagId } = action.payload
                for (const mid of Object.keys(state.tagsByMessageId)) {
                    state.tagsByMessageId[mid] = state.tagsByMessageId[mid].filter(t => t.id !== tagId)
                }
            })
    },
})

export const { clearAllMessageTags } = messageTagSlice.actions
export default messageTagSlice.reducer
